import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  Map, 
  Compass,
  Globe,
  TreeDeciduous,
  Leaf,
  Heart,
  Sparkles
} from 'lucide-react';

// Modules du tourisme
const tourismModules = [
  { 
    id: 'shaman-map', 
    name: 'Carte des Shamans', 
    description: 'Explorer les traditions shamaniques de Colombie et du monde',
    icon: Map,
    color: 'from-emerald-600 to-teal-600',
    borderColor: 'border-emerald-500',
    bgColor: 'bg-emerald-900/50',
    hoverShadow: 'rgba(16, 185, 129, 0.6)',
    route: '/tourism/shaman-map',
    active: true,
    features: [
      'Familles shamaniques',
      'Carte interactive',
      'Arbre patrimonial',
      'Respect éthique'
    ]
  },
  { 
    id: 'healing-traditions', 
    name: 'Traditions de Soin', 
    description: 'Découvrir les médecines ancestrales à travers les continents',
    icon: Heart,
    color: 'from-rose-600 to-pink-600',
    borderColor: 'border-rose-500',
    bgColor: 'bg-rose-900/50',
    hoverShadow: 'rgba(244, 63, 94, 0.6)',
    route: '/tourism/healing',
    active: false,
    features: [
      'Ayurveda',
      'Médecine chinoise',
      'Plantes sacrées',
      'Rituels mondiaux'
    ]
  },
  { 
    id: 'sacred-places', 
    name: 'Lieux Sacrés', 
    description: 'Voyager vers les sites de pouvoir et de transformation',
    icon: Compass,
    color: 'from-amber-600 to-orange-600',
    borderColor: 'border-amber-500',
    bgColor: 'bg-amber-900/50',
    hoverShadow: 'rgba(245, 158, 11, 0.6)',
    route: '/tourism/sacred-places',
    active: false,
    features: [
      'Sierra Nevada',
      'Amazonie',
      'Sites UNESCO',
      'Pèlerinages'
    ]
  }
];

const TourismHub = () => {
  const navigate = useNavigate();
  const [hoveredModule, setHoveredModule] = useState(null);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900">
      
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-emerald-300 hover:text-emerald-100"
            data-testid="back-to-bubbles"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour aux bulles
          </Button>
          
          <div className="flex items-center gap-3">
            <Badge className="bg-emerald-600/20 text-emerald-300 border border-emerald-500/50">
              <Globe className="w-4 h-4 mr-2" />
              Module Tourisme
            </Badge>
          </div>
        </div>
      </header>

      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-20">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <TreeDeciduous className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Bulle Tourisme
          </h1>
          <p className="text-xl text-emerald-400/70 max-w-2xl mx-auto">
            Explorez les traditions vivantes, les lieux sacrés et les chemins de guérison à travers le monde
          </p>
        </motion.div>

        {/* Modules Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl w-full">
          {tourismModules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.15 }}
              whileHover={{ 
                scale: 1.03, 
                boxShadow: `0 0 40px ${module.hoverShadow}` 
              }}
              onMouseEnter={() => setHoveredModule(module.id)}
              onMouseLeave={() => setHoveredModule(null)}
              onClick={() => module.active && navigate(module.route)}
              className={`cursor-pointer ${!module.active && 'opacity-60 cursor-not-allowed'}`}
              data-testid={`module-${module.id}`}
            >
              <Card className={`${module.bgColor} ${module.borderColor} border-2 backdrop-blur-sm transition-all duration-300 h-full`}>
                <CardContent className="p-6 flex flex-col min-h-[280px]">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${module.color} rounded-xl flex items-center justify-center shadow-lg`}>
                      <module.icon className="w-6 h-6 text-white" />
                    </div>
                    {!module.active && (
                      <Badge variant="outline" className="border-slate-500 text-slate-400 text-xs">
                        Bientôt
                      </Badge>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-bold text-white mb-2">
                    {module.name}
                  </h3>
                  <p className="text-slate-300 mb-4 text-sm">
                    {module.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-2 mb-4 flex-grow">
                    {module.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${module.color}`} />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <Button 
                    className={`w-full bg-gradient-to-r ${module.color} hover:opacity-90 transition-opacity mt-auto`}
                    disabled={!module.active}
                  >
                    {module.active ? 'Explorer' : 'Prochainement'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Citation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 text-center"
        >
          <p className="text-emerald-500/60 italic">
            "La graine est en train de germer, et c'est magnifique"
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TourismHub;
